import { createSelector } from "@reduxjs/toolkit";

import { deploymentSelector } from "Reducers/environment/deployment";

import type { DeploymentService, DeploymentServices } from "@packages/client";
import type { RootState } from "Store/configureStore";

const sortAlphabetically = <T>(
  list: T[] = [],
  accessor: (arg: T) => string = a => a as string,
  lang = "en"
) => list.sort((a, b) => accessor(a).localeCompare(accessor(b), lang));

const loadingSelector = (state: RootState) => state.deployment.loading;

const errorSelector = (state: RootState) => state.deployment.errors;

export const environmentDeploymentSelector = createSelector(
  deploymentSelector,
  (
    _: RootState,
    organizationId: string,
    projectId: string,
    environmentId: string
  ) => [organizationId, projectId, environmentId],
  (deployment, [organizationId, projectId, environmentId]) =>
    deployment.data?.[organizationId]?.[projectId]?.[environmentId]?.current
);

const transformServices = (
  services: DeploymentServices,
  kind: "webapp" | "service" | "worker"
) =>
  sortAlphabetically(
    Object.entries(services).map(([key, value]) => ({
      ...value,
      icon: value.type.split(":")[0],
      name: key,
      kind
    })),
    service => service.name
  );

export const environmentServicesSelector = createSelector(
  environmentDeploymentSelector,
  deployment => {
    if (!deployment) {
      return;
    }

    const webapps = transformServices(deployment.webapps, "webapp");
    const workers = transformServices(deployment.workers, "worker");
    const services = transformServices(deployment.services, "service");

    return Object.fromEntries(
      [...webapps, ...services, ...workers].map(service => [
        service.name,
        service
      ])
    );
  }
);

const servicesSelector = (
  state: RootState,
  {
    organizationId,
    projectId,
    environmentId
  }: { organizationId: string; projectId: string; environmentId: string }
) => {
  const deployments = deploymentSelector(state);
  const currentDeployment =
    deployments.data?.[organizationId]?.[projectId]?.[environmentId]?.current;

  if (!currentDeployment) {
    return {};
  }

  const webapps = currentDeployment.webapps;
  const workers = currentDeployment.workers;
  const services = Object.fromEntries(
    sortAlphabetically(
      Object.entries(currentDeployment.services),
      ([key]) => key
    )
  );

  return Object.fromEntries(
    Object.entries({
      ...webapps,
      ...services,
      ...workers
    }).map(([key, value]: [string, DeploymentService & { icon?: string }]) => {
      const icon = value?.type.split(":")[0];
      value.icon = icon;
      value.name = key;
      return [key, value];
    })
  );
};

const selector = createSelector(
  [loadingSelector, errorSelector, servicesSelector],
  (isLoading, error, services) => [services, isLoading, error] as const
);

export default selector;
